import React from 'react';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import { IconButton } from '../design-system';

interface TitleImageIndicatorProps {
  disabled?: boolean;
  onSelect: () => void;
  selected: boolean;
  title: string;
  /** Distance from the top left corner of the thumbnail, in pixels. */
  offset?: number;
}

const TITLE_IMAGE_INDICATOR_CLASS = 'title-image-indicator';
const SELECTED_CLASS = 'is-selected';

export const titleImageIndicatorRevealStyles = {
  [`& .${TITLE_IMAGE_INDICATOR_CLASS}`]: {
    opacity: 0,
    pointerEvents: 'none',
  },
  [`&:hover .${TITLE_IMAGE_INDICATOR_CLASS}, & .${TITLE_IMAGE_INDICATOR_CLASS}:focus-visible, & .${TITLE_IMAGE_INDICATOR_CLASS}.${SELECTED_CLASS}`]: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  '@media (hover: none)': {
    [`& .${TITLE_IMAGE_INDICATOR_CLASS}`]: {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
};

const buttonStyles = {
  position: 'absolute',
  transition:
    'opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease',
  '&:hover': {
    color: 'warning.main',
  },
  [`&.${SELECTED_CLASS}, &.${SELECTED_CLASS}:hover, &.${SELECTED_CLASS}.Mui-disabled`]: {
    color: 'warning.main',
  },
};
const getPositionStyles = (offset: number) => ({ top: offset, left: offset });

const TitleImageIndicator = React.memo(
  ({
    disabled = false,
    onSelect,
    selected,
    title,
    offset = 4,
  }: TitleImageIndicatorProps) => {
    const positionStyles = getPositionStyles(offset);
    const combinedStyles = { ...buttonStyles, ...positionStyles };
    const className = `${TITLE_IMAGE_INDICATOR_CLASS}${
      selected ? ` ${SELECTED_CLASS}` : ''
    }`;
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onSelect();
    };
    const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
    };

    return (
      <IconButton
        aria-label={title}
        className={className}
        density="compact"
        disabled={disabled || selected}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        sx={combinedStyles}
        title={title}
        tone="warning"
        variant="overlay"
      >
        {selected ? <StarIcon /> : <StarBorderIcon />}
      </IconButton>
    );
  }
);

TitleImageIndicator.displayName = 'TitleImageIndicator';

export { TitleImageIndicator };
